import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import usersApi from '../../../api/usersApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

const baseSchema = {
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  email: yup.string().trim().email('Ingresa un correo válido').required('El correo es obligatorio').max(255),
  phone: yup.string().nullable(),
  role_id: yup.number().typeError('Selecciona un rol').required('Selecciona un rol'),
};

const createSchema = yup.object({
  ...baseSchema,
  password: yup.string().min(8, 'Debe tener al menos 8 caracteres').required('La contraseña es obligatoria'),
});

const editSchema = yup.object({
  ...baseSchema,
  password: yup.string().min(8, 'Debe tener al menos 8 caracteres').nullable().transform((v) => v || undefined),
});

const emptyValues = {
  name: '',
  email: '',
  phone: '',
  role_id: '',
  password: '',
};

// Formulario de alta/edicion de un usuario y su rol. `user` nulo significa "crear nuevo".
export default function UserFormDialog({ open, onClose, onSaved, user, roles }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(user);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(isEditing ? editSchema : createSchema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setShowPassword(false);
    reset(
      user
        ? {
            name: user.name,
            email: user.email || '',
            phone: user.phone || '',
            role_id: user.role_id || '',
            password: '',
          }
        : emptyValues
    );
  }, [open, user, reset]);

  const onSubmit = async (data) => {
    const payload = { ...data };
    if (isEditing && !payload.password) {
      delete payload.password;
    }

    try {
      if (isEditing) {
        await usersApi.updateUser(user.id, payload);
        enqueueSnackbar('Usuario actualizado correctamente.', { variant: 'success' });
      } else {
        await usersApi.createUser(payload);
        enqueueSnackbar('Usuario creado correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el usuario.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="user-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="Nombre completo"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Correo electrónico"
            type="email"
            fullWidth
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            {...register('email')}
          />

          <TextField
            label="Teléfono"
            fullWidth
            error={Boolean(errors.phone)}
            helperText={errors.phone?.message}
            {...register('phone')}
          />

          <Controller
            name="role_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Rol"
                fullWidth
                error={Boolean(errors.role_id)}
                helperText={errors.role_id?.message}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <TextField
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            error={Boolean(errors.password)}
            helperText={
              errors.password?.message || (isEditing ? 'Déjala en blanco para no cambiarla.' : undefined)
            }
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            {...register('password')}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="user-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UserFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  user: PropTypes.object,
  roles: PropTypes.array.isRequired,
};

UserFormDialog.defaultProps = {
  user: null,
};
